import CalendarIcon from '@mds/icons/CalendarBlank';
import CaretDown from '@mds/icons/CaretDown';
import CaretLeft from '@mds/icons/CaretLeft';
import CaretRight from '@mds/icons/CaretRight';
import { iconSizes } from '@mds/tokens';
import { buttonClasses, menuItemClasses, type Components } from '@mui/material';
import { pickersArrowSwitcherClasses } from '@mui/x-date-pickers/internals';
import { pickerDayClasses } from '@mui/x-date-pickers/PickerDay';
import { pickersCalendarHeaderClasses } from '@mui/x-date-pickers/PickersCalendarHeader';
import { pickersFilledInputClasses } from '@mui/x-date-pickers/PickersTextField';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { monthCalendarClasses, MonthCalendarClasses } from '@mui/x-date-pickers/MonthCalendar';
import { yearCalendarClasses, YearCalendarClasses } from '@mui/x-date-pickers/YearCalendar';
import OpenPickerButton from '../../components/_internal/OpenPickerButton';
import { themeVars } from '../themeVars';
import {
  filledInputSingleLineStyles,
  getFilledInputUnderlineStyles,
  mediumSizeTypography,
  smallSizeTypography,
} from './inputs';
import { typographyStyles, type Theme } from './utils';

/**
 * Date picker inputs do not use MUI's TextField component due to different accessibility
 * and behavior requirements. Their field metrics reuse the shared styles from ./inputs.ts.
 */

const timeOptionStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius,

  [`&.${menuItemClasses.selected}`]: {
    '&, &:focus, &:focus-visible, &:hover': {
      backgroundColor: themeVars(theme).palette.action.selectedContrast,
      color: themeVars(theme).palette.text.contrast,
    },
  },

  [`&.${menuItemClasses.selected}.${menuItemClasses.focusVisible}`]: {
    backgroundColor: themeVars(theme).palette.action.selectedContrast,
  },
});

export const MuiDateField: Components<Theme>['MuiDateField'] = {
  defaultProps: {
    variant: 'filled',
  },
};

export const MuiDateTimeField: Components<Theme>['MuiDateTimeField'] = {
  defaultProps: {
    variant: 'filled',
  },
};

export const MuiTimeField: Components<Theme>['MuiTimeField'] = {
  defaultProps: {
    variant: 'filled',
  },
};

export const MuiDesktopDatePicker: Components<Theme>['MuiDesktopDatePicker'] = {
  defaultProps: {
    slots: {
      openPickerIcon: CalendarIcon,
      openPickerButton: OpenPickerButton,
    },
  },
};

export const MuiPickerPopper: Components<Theme>['MuiPickerPopper'] = {
  defaultProps: {
    // @ts-expect-error not exported from MUI
    slotProps: {
      popper: {
        modifiers: [{ name: 'offset', options: { offset: [0, 2] } }],
      },
    },
  },
  styleOverrides: {
    paper: ({ theme }) => ({
      boxShadow: 'none',
      border: '1px solid',
      borderColor: themeVars(theme).palette.border.subtle,
      backgroundColor: themeVars(theme).palette.background.default,
    }),
  },
};

export const MuiPickersLayout: Components<Theme>['MuiPickersLayout'] = {
  styleOverrides: {
    actionBar: ({ theme }) => ({
      padding: theme.spacing(0.5),

      [`& .${buttonClasses.root}`]: {
        ...typographyStyles(theme.typography.label3),
        minWidth: 0,
        minHeight: 28,
        paddingBlock: 6,
        paddingInline: 8,
      },
    }),
    root: {
      variants: [
        {
          props: { pickerOrientation: 'portrait', hasShortcuts: false },
          style: {
            gridAutoColumns: '0 max-content max-content',
          },
        },
      ],
    },
  },
};

export const MuiPickersFilledInput: Components<Theme>['MuiPickersFilledInput'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      [`&, &.${pickersFilledInputClasses.focused}, &.${pickersFilledInputClasses.disabled}, &:hover`]: {
        backgroundColor: themeVars(theme).palette.action.enabled,
        '@media (hover: none)': {
          backgroundColor: themeVars(theme).palette.action.enabled,
        },
      },
      ...getFilledInputUnderlineStyles({
        theme,
        classes: {
          focused: pickersFilledInputClasses.focused,
          disabled: pickersFilledInputClasses.disabled,
          error: pickersFilledInputClasses.error,
        },
      }),

      variants: [
        {
          props: ({ endAdornment }) => !!endAdornment,
          style: {
            paddingRight: 16,
          },
        },
      ],
    }),

    sectionsContainer: {
      ...filledInputSingleLineStyles.medium,

      variants: [
        {
          props: { inputSize: 'small' },
          style: {
            ...filledInputSingleLineStyles.small,
          },
        },
        {
          props: { hiddenLabel: true },
          style: {
            ...filledInputSingleLineStyles.mediumHiddenLabel,
          },
        },
        {
          props: { hiddenLabel: true, inputSize: 'small' },
          style: {
            ...filledInputSingleLineStyles.smallHiddenLabel,
          },
        },
      ],
    },
  },
};

export const MuiPickersInputBase: Components<Theme>['MuiPickersInputBase'] = {
  styleOverrides: {
    sectionsContainer: ({ theme }) => ({
      ...mediumSizeTypography(theme),

      '&::placeholder': {
        opacity: 1,
        color: themeVars(theme).palette.text.secondary,
      },

      variants: [
        {
          props: { inputSize: 'small' },
          style: {
            ...smallSizeTypography(theme),
          },
        },
      ],
    }),
  },
};

export const MuiDigitalClock: Components<Theme>['MuiDigitalClock'] = {
  styleOverrides: {
    item: ({ theme }) => timeOptionStyles(theme),
  },
};

export const MuiMultiSectionDigitalClockSection: Components<Theme>['MuiMultiSectionDigitalClockSection'] = {
  styleOverrides: {
    item: ({ theme }) => timeOptionStyles(theme),
  },
};

export const MuiPickersCalendarHeader: Components<Theme>['MuiPickersCalendarHeader'] = {
  defaultProps: {
    slots: {
      switchViewIcon: CaretDown,
    },
  },
  styleOverrides: {
    root: {
      paddingLeft: 32,
      paddingRight: 18,
    },
    switchViewButton: ({ theme }) => ({
      width: 32,
      height: 32,
      '&:focus-visible': {
        boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
      },
      [`& .${pickersCalendarHeaderClasses.switchViewIcon}`]: {
        fontSize: iconSizes.md,
      },
    }),
  },
};

export const MuiPickersArrowSwitcher: Components<Theme>['MuiPickersArrowSwitcher'] = {
  defaultProps: {
    slots: {
      leftArrowIcon: CaretLeft,
      rightArrowIcon: CaretRight,
    },
  },
  styleOverrides: {
    button: ({ theme }) => ({
      width: 32,
      height: 32,
      '&:focus-visible': {
        boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
      },
      [`& .${pickersArrowSwitcherClasses.rightArrowIcon}, & .${pickersArrowSwitcherClasses.leftArrowIcon}`]: {
        fontSize: iconSizes.md,
      },
    }),
  },
};

export const MuiPickerDay: Components<Theme>['MuiPickerDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,

      '@media (hover: hover)': {
        '&:hover': {
          backgroundColor: themeVars(theme).palette.action.hover,
        },
      },
      '&:active': {
        backgroundColor: themeVars(theme).palette.action.pressed,
      },
      '&:focus': {
        backgroundColor: 'transparent',
      },
      '&:focus-visible': {
        borderColor: themeVars(theme).palette.border.focused,
        // We should just be able to use this box shadow, unfortunately it renders poorly in
        // Chrome. The border radius is not smooth and the thickness varies based on element
        // it's applied to.
        boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
      },
      [`&.${pickerDayClasses.selected}`]: {
        '&, &:focus, &:hover': {
          background: themeVars(theme).palette.action.selectedContrast,
          color: themeVars(theme).palette.text.contrast,
        },
      },
      [`&.${pickerDayClasses.disabled}`]: {
        color: themeVars(theme).palette.text.disabled,
      },
    }),

    today: ({ theme }) => ({
      [`&:not(.${pickerDayClasses.selected})`]: {
        border: 0,
      },
      ':before': {
        content: '""',
        position: 'absolute',
        width: 16,
        height: '100%',
        pointerEvents: 'none',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 6,
        borderBlockEnd: `2px solid ${themeVars(theme).palette.primary.main}`,
        zIndex: 2,
      },
    }),
  },
};

const stylesViewsButton = (
  classes: Record<keyof (MonthCalendarClasses | YearCalendarClasses), string>,
  theme: Theme,
) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: theme.typography.body1.fontWeight,
  [`&.${classes.selected}`]: {
    '&, &:focus, &:hover': {
      background: themeVars(theme).palette.action.selectedContrast,
      color: themeVars(theme).palette.text.contrast,
    },
  },
  [`&.${classes.disabled}`]: {
    color: themeVars(theme).palette.text.disabled,
  },
});

export const MuiMonthCalendar: Components<Theme>['MuiMonthCalendar'] = {
  styleOverrides: {
    button: ({ theme }) => stylesViewsButton(monthCalendarClasses, theme),
  },
};

export const MuiYearCalendar: Components<Theme>['MuiYearCalendar'] = {
  styleOverrides: {
    button: ({ theme }) => stylesViewsButton(yearCalendarClasses, theme),
  },
};
